import { useState, useCallback } from 'react';
import { apiClient, QuizData, QuizResults } from '../services';

export const useQuizState = () => {
  const [quizData, setQuizData] = useState<QuizData | null>(null);
  const [quizResults, setQuizResults] = useState<QuizResults | null>(null);
  const [isLoadingQuiz, setIsLoadingQuiz] = useState(false);
  const [quizError, setQuizError] = useState<string | null>(null);

  const generateQuiz = useCallback(async (fileId: string) => {
    setIsLoadingQuiz(true);
    setQuizError(null);

    try {
      const data = await apiClient.generateQuizFromFile(fileId);
      setQuizData(data);
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate quiz from file';
      setQuizError(errorMessage);
      throw error;
    } finally {
      setIsLoadingQuiz(false);
    }
  }, []);

  const submitQuiz = useCallback(async (quizId: string, answers: string[]) => {
    setIsLoadingQuiz(true);
    setQuizError(null);

    try {
      const results = await apiClient.submitQuiz({ quiz_id: quizId, answers });
      setQuizResults(results);
      return results;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit quiz';
      setQuizError(errorMessage);
      throw error;
    } finally {
      setIsLoadingQuiz(false);
    }
  }, []);

  const resetQuiz = useCallback(() => {
    setQuizData(null);
    setQuizResults(null);
    setQuizError(null);
  }, []);

  const setQuizDataDirectly = useCallback((data: QuizData) => {
    setQuizData(data);
    setQuizResults(null); // Clear previous results when setting new quiz
    setQuizError(null);
  }, []);

  return {
    quizData,
    quizResults,
    isLoadingQuiz,
    quizError,
    generateQuiz,
    submitQuiz,
    resetQuiz,
    setQuizData: setQuizDataDirectly,
  };
};